import { useCallback, useMemo } from 'react'

import {
	type EntityCollectionItem,
	type EntityDefinition,
} from '#app/routes/mocra+/_form+/entity-definition.ts'

import { type UseSectionFormResult } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'

type UseEntityCollectionOptions<Values, Entry, Draft> = {
	readonly form: UseSectionFormResult<Values>
	readonly fieldPath: string
	readonly definition: EntityDefinition<Entry, Draft>
	readonly getFieldErrorPaths?: (index: number) => readonly string[]
}

type ReorderOrder = readonly number[]

export type EntityCollectionResult<Entry, Draft> = {
	readonly items: readonly EntityCollectionItem<Entry, Draft>[]
	readonly append: (draft: Draft) => void
	readonly replace: (index: number, draft: Draft) => void
	readonly remove: (index: number) => void
	readonly reorder: (order: ReorderOrder) => void
	readonly createDraft: () => Draft
	readonly sanitizeDraft: (draft: Draft) => Draft
	readonly entries: readonly Entry[]
}

export function useEntityCollection<Values, Entry, Draft>(
	options: UseEntityCollectionOptions<Values, Entry, Draft>,
): EntityCollectionResult<Entry, Draft> {
	const { form, fieldPath, definition, getFieldErrorPaths } = options
	const {
		schema,
		createDraft,
		sanitizeDraft,
		serialize,
		deserialize,
		collectErrors,
	} = definition

	const parseEntry = useCallback(
		(candidate: unknown): Entry | null => {
			const result = schema.safeParse(candidate)
			if (!result.success) {
				return null
			}
			return result.data as Entry
		},
		[schema],
	)

	const entries = useMemo<Entry[]>(() => {
		const value = form.getFieldValue<unknown>(fieldPath)
		if (!Array.isArray(value)) return []
		const next: Entry[] = []
		for (const candidate of value) {
			const entry = parseEntry(candidate)
			if (entry) {
				next.push(entry)
			}
		}
		return next
	}, [fieldPath, form, parseEntry])

	const collectFieldErrors = useCallback(
		(index: number): readonly string[] => {
			if (!getFieldErrorPaths) return []
			const paths = getFieldErrorPaths(index)
			if (!paths || paths.length === 0) return []
			const messages = new Set<string>()
			for (const path of paths) {
				const errors = form.getFieldErrors(path)
				for (const error of errors) {
					if (error) {
						messages.add(error)
					}
				}
			}
			return [...messages]
		},
		[form, getFieldErrorPaths],
	)

	const items = useMemo<readonly EntityCollectionItem<Entry, Draft>[]>(() => {
		return entries.map((entry, index) => {
			const draft = deserialize(entry)
			const draftErrors = collectErrors?.(draft) ?? []
			const fieldErrors = collectFieldErrors(index)
			const errorSet = new Set<string>()
			for (const message of draftErrors) {
				if (message) {
					errorSet.add(message)
				}
			}
			for (const message of fieldErrors) {
				if (message) {
					errorSet.add(message)
				}
			}
			return {
				index,
				entry,
				draft,
				errors: [...errorSet],
			}
		})
	}, [collectFieldErrors, collectErrors, deserialize, entries])

	const ensureArray = useCallback(
		(previous: unknown): Entry[] => {
			if (Array.isArray(previous)) {
				return previous as Entry[]
			}
			return entries
		},
		[entries],
	)

	const draftToEntry = useCallback(
		(draft: Draft): Entry | null => {
			const sanitizedDraft = sanitizeDraft(draft)
			const serializedEntry = serialize(sanitizedDraft)
			const result = schema.safeParse(serializedEntry)
			if (!result.success) {
				console.warn('useEntityCollection: skipping invalid entry', {
					errors: result.error.flatten(),
				})
				return null
			}
			return result.data as Entry
		},
		[sanitizeDraft, serialize, schema],
	)

	const append = useCallback(
		(draft: Draft) => {
			const entry = draftToEntry(draft)
			if (!entry) return
			form.updateFieldValue(fieldPath, (previous: unknown) => {
				const list = ensureArray(previous)
				return [...list, entry]
			})
		},
		[draftToEntry, ensureArray, fieldPath, form],
	)

	const replace = useCallback(
		(index: number, draft: Draft) => {
			const entry = draftToEntry(draft)
			if (!entry) return
			form.updateFieldValue(fieldPath, (previous: unknown) => {
				const list = ensureArray(previous)
				if (!list[index]) return list
				return list.map((current, entryIndex) =>
					entryIndex === index ? entry : current,
				)
			})
		},
		[draftToEntry, ensureArray, fieldPath, form],
	)

	const remove = useCallback(
		(index: number) => {
			form.updateFieldValue(fieldPath, (previous: unknown) => {
				const list = ensureArray(previous)
				if (!list[index]) return list
				return list.filter((_, entryIndex) => entryIndex !== index)
			})
		},
		[ensureArray, fieldPath, form],
	)

	const reorder = useCallback(
		(order: ReorderOrder) => {
			form.updateFieldValue(fieldPath, (previous: unknown) => {
				const list = ensureArray(previous)
				if (order.length !== list.length) {
					return list
				}
				const next: Entry[] = []
				for (const position of order) {
					const entry = list[position]
					if (entry !== undefined) {
						next.push(entry)
					}
				}
				return next.length === list.length ? next : list
			})
		},
		[ensureArray, fieldPath, form],
	)

	return {
		items,
		append,
		replace,
		remove,
		reorder,
		createDraft,
		sanitizeDraft,
		entries,
	}
}
